import React from "react"

import { Badge, type BadgeProps } from "@coval/ui/badge"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { AnomalyEventList } from "../components/AnomalyEventList"
import { getCardStyle } from "../components/deepDiveCardStyles"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"

interface VoiceQualityRawValues {
  cpps_mean_db?: number
  jitter_local?: number
  shimmer_local?: number
  f0_std_hz?: number
  naturalness_score?: number
}

const CPPS_MIDPOINT_DB = 10.0
const CPPS_K = -0.5
const JITTER_NORMAL = 1.04
const JITTER_INSTABILITY = 2.0
const SHIMMER_NORMAL = 3.81
const SHIMMER_INSTABILITY = 6.0
const F0_STD_MONOTONE_HZ = 15.0
const VOICE_QUALITY_SEVERITY_THRESHOLD = 0.5

function sigmoid(x: number, midpoint: number, k: number): number {
  const exponent = Math.max(-500, Math.min(500, k * (x - midpoint)))
  return 1.0 / (1.0 + Math.exp(exponent))
}

function cppsScore(db: number): number {
  return sigmoid(db, CPPS_MIDPOINT_DB, CPPS_K)
}

function jitterScore(pct: number): number {
  if (pct <= JITTER_NORMAL) {
    return 1.0
  }
  if (pct >= JITTER_INSTABILITY) {
    return 0.0
  }
  return 1.0 - (pct - JITTER_NORMAL) / (JITTER_INSTABILITY - JITTER_NORMAL)
}

function shimmerScore(pct: number): number {
  if (pct <= SHIMMER_NORMAL) {
    return 1.0
  }
  if (pct >= SHIMMER_INSTABILITY) {
    return 0.0
  }
  return 1.0 - (pct - SHIMMER_NORMAL) / (SHIMMER_INSTABILITY - SHIMMER_NORMAL)
}

function f0Score(stdHz: number): number {
  if (stdHz <= 0) {
    return 0.0
  }
  return Math.min(1.0, stdHz / F0_STD_MONOTONE_HZ)
}

function scoreColor(s: number): string {
  if (s >= 0.7) {
    return "bg-surface-2"
  }
  if (s >= 0.4) {
    return "bg-orange-400"
  }
  return "bg-red-400"
}

function scoreLabel(s: number): string {
  if (s >= 0.7) {
    return "Good"
  }
  if (s >= 0.4) {
    return "Fair"
  }
  return "Poor"
}

export class VoiceQualityMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = "score"
  aggregate_units = "score"

  renderMetricType(): string {
    return "Voice Quality"
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const value = mo.float_value ?? 0
    const resultData = (mo.result as Record<string, unknown> | undefined)
      ?.raw_values as VoiceQualityRawValues | undefined

    let qualityLabel = ""
    let qualityVariant: NonNullable<BadgeProps["variant"]> = "default"

    if (value >= 0.8) {
      qualityLabel = "Excellent"
      qualityVariant = "success"
    } else if (value >= 0.6) {
      qualityLabel = "Good"
      qualityVariant = "info"
    } else if (value >= 0.4) {
      qualityLabel = "Fair"
      qualityVariant = "warning"
    } else {
      qualityLabel = "Poor"
      qualityVariant = "danger"
    }

    const cpps = resultData?.cpps_mean_db
    const jitter = resultData?.jitter_local
    const shimmer = resultData?.shimmer_local
    const f0Std = resultData?.f0_std_hz
    const subvalues = mo.subvalues_by_timestamp ?? []
    const card = getCardStyle(1 - value, 0.3)

    const features: Array<{
      label: string
      measured: string
      score: number | null
      barFill: number | null
      weight: number
    }> = [
      {
        label: "CPPS",
        measured: cpps != null ? `${cpps.toFixed(1)} dB` : "--",
        score: cpps != null ? cppsScore(cpps) : null,
        barFill: cpps != null ? cppsScore(cpps) : null,
        weight: 0.4,
      },
      {
        label: "Jitter",
        measured: jitter != null ? `${jitter.toFixed(2)}%` : "--",
        score: jitter != null ? jitterScore(jitter) : null,
        barFill: jitter != null ? jitterScore(jitter) : null,
        weight: 0.2,
      },
      {
        label: "Shimmer",
        measured: shimmer != null ? `${shimmer.toFixed(2)}%` : "--",
        score: shimmer != null ? shimmerScore(shimmer) : null,
        barFill: shimmer != null ? shimmerScore(shimmer) : null,
        weight: 0.2,
      },
      {
        label: "F0 Std Dev",
        measured: f0Std != null ? `${f0Std.toFixed(1)} Hz` : "--",
        score: f0Std != null ? f0Score(f0Std) : null,
        barFill: f0Std != null ? f0Score(f0Std) : null,
        weight: 0.2,
      },
    ]

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl font-bold">{value.toFixed(2)}</span>
          <Badge variant={qualityVariant}>{qualityLabel}</Badge>
        </div>

        <div className={`p-3 rounded-lg ${card.bg} space-y-3`}>
          <p className="text-xs text-coval-text-muted">
            Composite naturalness score from four dimensions. Higher is better.
          </p>
          <div className="space-y-3">
            {features.map((f) => (
              <div key={f.label} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-coval-text-muted font-medium">
                    {f.label}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-coval-text-muted">
                      {f.measured}
                    </span>
                    <span className="text-xs text-coval-text-muted w-8 text-right">
                      {f.score != null ? scoreLabel(f.score) : "--"}
                    </span>
                  </div>
                </div>
                {f.score != null ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-surface-2 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${scoreColor(f.score)}`}
                        style={{
                          width: `${((f.barFill ?? 0) * 100).toFixed(0)}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs font-mono text-coval-text-muted w-8 text-right">
                      {f.score.toFixed(2)}
                    </span>
                  </div>
                ) : (
                  <div className="text-xs text-coval-text-muted italic">
                    No data available
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <AnomalyEventList
          subvalues={subvalues}
          severityThreshold={VOICE_QUALITY_SEVERITY_THRESHOLD}
          chipLabel="Quality"
          title="Quality Events"
          groupByLabel
          metadataFields={[
            {
              key: "jitter_local_percent",
              label: "jitter",
              format: (v: number) => `${v.toFixed(1)}%`,
              colorFn: (v: number) =>
                v >= 2.0
                  ? "text-red-600 dark:text-red-400"
                  : v >= 1.5
                    ? "text-orange-600 dark:text-orange-400"
                    : "text-coval-text-muted",
            },
            {
              key: "shimmer_local_percent",
              label: "shimmer",
              format: (v: number) => `${v.toFixed(1)}%`,
              colorFn: (v: number) =>
                v >= 6.0
                  ? "text-red-600 dark:text-red-400"
                  : v >= 4.0
                    ? "text-orange-600 dark:text-orange-400"
                    : "text-coval-text-muted",
            },
          ]}
        />
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message) {
      return []
    }
    try {
      const subvalues = mo.subvalues_by_timestamp ?? []
      return subvalues
        .filter(
          (item) => (item.float_value ?? 0) >= VOICE_QUALITY_SEVERITY_THRESHOLD,
        )
        .map((item, idx) => {
          const severity = item.float_value ?? 0
          const alpha = Math.min(0.2 + (severity / 5.0) * 0.5, 0.7)
          return {
            id: `voice_quality:${idx}`,
            start: item.start_offset,
            end: Math.max(item.end_offset, item.start_offset + 0.5),
            color: `rgba(20, 184, 166, ${alpha.toFixed(2)})`,
            drag: false,
            resize: false,
          }
        })
    } catch {
      return []
    }
  }
}
